// src/components/admin/BookingsAIAgent.jsx
// "Bookings AI" page under Bookings Management - the salon's only booking assistant. The admin types an instruction
// ("Book Albert Wee for a Wash and Blow on 2 Oct, any stylist") and the agent carries it out, or asks a question
// ("Which staff have the best utilisation?") and gets data-backed insights. Sensitive actions come back as approval
// cards that only run when the admin presses Approve.
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { BookingsAgentService } from '../../api/backendAiService';
import { notifyCustomerOfAppointmentStatus } from '../../utils/bookingNotifications';
import DailyTokenCounter from './DailyTokenCounter';
import '../../styles/bookings-ai-agent.css';

const ACTION_EXAMPLES = [
  'Make a booking',
  'Book Albert Wee for a Wash and Blow on 2 Oct. Any available stylist is fine.',
  'Who is free tomorrow afternoon for a haircut?',
  'Approve all pending booking requests for this Saturday.',
  "Move Sarah Tan's appointment on Friday to 3pm.",
  "Cancel John Lim's booking next Monday - he is unwell.",
];

const INSIGHT_EXAMPLES = [
  'Analyze my booking patterns and identify peak hours',
  'Which staffs have the best utilization rates?',
  'Suggest strategies to reduce no-shows and cancellations',
  'What services should we bundle to increase average booking value?',
  "Which customers haven't been back in the last 3 months?",
];

const APPROVAL_RULES = [
  'Cancelling a booking',
  'Rejecting a booking request',
  'Booking at a price other than the listed price',
  'More than 5 changes from one instruction',
];

// Fire the customer push + e-mail for a completed action (same helper and settings as the Calendar)
const sendNotification = (salonId, notify) => {
  if (!notify) return;
  notifyCustomerOfAppointmentStatus({
    salonId,
    appointmentId: notify.appointmentId,
    status: notify.status,
    customerEmail: notify.customerEmail,
    customerName: notify.customerName,
    serviceName: notify.serviceName,
    staffName: notify.staffName,
    appointmentDateTime: new Date(notify.dateTimeISO)
  });
};

function BookingsAIAgent({ salonId, onOpenCalendar }) {
  const [messages, setMessages] = useState([{
    id: 'welcome',
    type: 'ai',
    content: "Tell me what you'd like done in Bookings - for example booking a customer in, approving requests, moving or cancelling an appointment. I check availability (opening hours, rosters, leave and existing bookings) before making any change, and anything sensitive waits for your approval. You don't need to give every detail up front - I'll ask for whatever is missing.\n\nYou can also ask me anything about your bookings - peak times, staff utilisation, popular services, frequent or lapsed customers, cancellation and no-show rates - and I'll answer from your full booking history with recommendations to optimise your bookings and scheduling.",
    timestamp: new Date()
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  // actionId -> { state: 'pending' | 'working' | 'done' | 'declined' | 'failed', message }
  const [approvals, setApprovals] = useState({});
  const [isListening, setIsListening] = useState(false);
  const serviceRef = useRef(null);
  const recognitionRef = useRef(null);
  const tokenCounterRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Approve/decline outcomes the agent hasn't been told about yet; sent along with the next instruction
  const unreportedOutcomes = useRef([]);

  useEffect(() => {
    if (salonId) serviceRef.current = new BookingsAgentService(salonId);
  }, [salonId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isLoading]);

  // Voice input (browser speech recognition, where supported)
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => (prev ? `${prev} ${transcript}` : transcript));
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    return () => recognition.abort();
  }, []);

  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const addMessage = (msg) => setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, timestamp: new Date(), ...msg }]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading || limitReached || !serviceRef.current) return;

    addMessage({ type: 'user', content: text });
    setInput('');
    setIsLoading(true);

    const outcomes = unreportedOutcomes.current;
    unreportedOutcomes.current = [];
    const outgoing = outcomes.length
      ? `[Update since your last reply: ${outcomes.join(' ')}]\n\n${text}`
      : text;

    try {
      const res = await serviceRef.current.sendMessage(outgoing);
      res.actions.forEach(a => sendNotification(salonId, a.notify));
      setApprovals(prev => {
        const next = { ...prev };
        res.pendingActions.forEach(p => { next[p.actionId] = { state: 'pending' }; });
        return next;
      });
      addMessage({ type: 'ai', content: res.message, actions: res.actions, pendingActions: res.pendingActions });
      if (res.dailyUsage) tokenCounterRef.current?.update(res.dailyUsage);
    } catch (error) {
      console.error('Bookings AI agent error:', error);
      unreportedOutcomes.current = [...outcomes, ...unreportedOutcomes.current];
      const content = error?.status === 429
        ? 'You have reached the daily AI usage limit. Bookings AI is available again after midnight.'
        : error?.status === 403
          ? (error.message || 'Bookings AI is not available for your account.')
          : "Something went wrong and I couldn't finish. Please check Calendar Management before trying again - some changes may already have been made.";
      addMessage({ type: 'system', content });
    } finally {
      setIsLoading(false);
      tokenCounterRef.current?.refresh();
    }
  };

  const handleDecision = async (action, decision) => {
    setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'working' } }));
    try {
      const res = await serviceRef.current.resolveAction(action.actionId, decision);
      if (res.declined) {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'declined', message: 'Declined - nothing was changed.' } }));
        unreportedOutcomes.current.push(`The admin DECLINED "${action.title}", so it was not done.`);
      } else if (res.success) {
        sendNotification(salonId, res.notify);
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'done', message: res.message, warnings: res.warnings } }));
        unreportedOutcomes.current.push(`The admin APPROVED "${action.title}" and it was completed: ${res.message}`);
      } else {
        setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: res.message } }));
        unreportedOutcomes.current.push(`The admin approved "${action.title}" but it failed: ${res.message}`);
      }
    } catch (error) {
      setApprovals(prev => ({ ...prev, [action.actionId]: { state: 'failed', message: error.message } }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (t) => t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const renderApprovalCard = (p) => {
    const status = approvals[p.actionId] || { state: 'pending' };
    return (
      <div key={p.actionId} className={`bai-approval-card bai-approval-card--${status.state}`}>
        <div className="bai-approval-head">
          <span className="bai-approval-badge">Needs your approval</span>
          <strong>{p.title}</strong>
        </div>
        {p.lines?.map((line, i) => <div key={i} className="bai-approval-line">{line}</div>)}
        <div className="bai-approval-reason">{p.reason}</div>
        {(status.state === 'pending' || status.state === 'working') && (
          <div className="bai-approval-actions">
            <button className="bai-btn bai-btn--approve" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'approve')}>
              {status.state === 'working' ? 'Working…' : 'Approve'}
            </button>
            <button className="bai-btn bai-btn--decline" disabled={status.state === 'working'} onClick={() => handleDecision(p, 'decline')}>
              Decline
            </button>
          </div>
        )}
        {status.state === 'done' && <div className="bai-approval-result bai-approval-result--ok">✓ {status.message}</div>}
        {status.state === 'declined' && <div className="bai-approval-result">{status.message}</div>}
        {status.state === 'failed' && <div className="bai-approval-result bai-approval-result--err">Not done: {status.message}</div>}
      </div>
    );
  };

  return (
    <div className="ai-tab-container bookings-ai-agent">
      <div className="ai-tab-header">
        <h2>Bookings AI</h2>
        <p>Type an instruction and the assistant will carry it out in Bookings Management - checking availability first and notifying the customer, just like doing it by hand. Or ask a question to get AI-powered insights for optimising your booking operations and scheduling.</p>
      </div>

      <DailyTokenCounter ref={tokenCounterRef} salonId={salonId} onLimitReached={setLimitReached} />

      <div className="ai-interaction-panel">
        <div className="ai-prompt-examples">
          <h3>Try an instruction</h3>
          <div className="example-prompts">
            {ACTION_EXAMPLES.map(ex => (
              <div key={ex} className="prompt-card" onClick={() => setInput(ex)}>"{ex}"</div>
            ))}
          </div>
          <h3 className="bai-examples-heading">Or ask for insights</h3>
          <div className="example-prompts">
            {INSIGHT_EXAMPLES.map(ex => (
              <div key={ex} className="prompt-card" onClick={() => setInput(ex)}>"{ex}"</div>
            ))}
          </div>
          <div className="bai-rules">
            <h4>Always asks for your approval</h4>
            <ul>{APPROVAL_RULES.map(r => <li key={r}>{r}</li>)}</ul>
            <p>Payments, wallet, points and vouchers are not handled here - use the customer's page.</p>
          </div>
        </div>

        <div className="ai-chat-interface">
          <div className="chat-messages">
            {messages.map(m => (
              <div key={m.id} className={`message ${m.type}-message`}>
                {m.type === 'ai' && <div className="ai-avatar">🤖</div>}
                {m.type === 'user' && <div className="user-avatar">👤</div>}
                {m.type === 'system' && <div className="system-avatar">ℹ️</div>}
                <div className="message-content">
                  <div className="message-text"><ReactMarkdown>{m.content}</ReactMarkdown></div>
                  {m.actions?.length > 0 && (
                    <div className="bai-done-list">
                      {m.actions.map((a, i) => (
                        <div key={i} className="bai-done-item">
                          <span className="bai-done-check">✓</span>
                          <span>
                            {a.message}
                            {a.warnings?.map((w, j) => <span key={j} className="bai-warning">⚠ {w}</span>)}
                          </span>
                        </div>
                      ))}
                      {onOpenCalendar && (
                        <button className="bai-link" onClick={onOpenCalendar}>Open Calendar Management →</button>
                      )}
                    </div>
                  )}
                  {m.pendingActions?.map(renderApprovalCard)}
                  <div className="message-time">{formatTime(m.timestamp)}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={limitReached ? 'Daily AI usage limit reached. Resets at midnight.' : 'e.g. "Book Albert Wee for a Wash and Blow on 2 Oct" or "What are our peak hours?" (Enter to send, Shift+Enter for a new line)'}
              rows="3"
              disabled={isLoading || limitReached}
            />
            <div className="input-actions">
              <button
                className={`voice-input-btn ${isListening ? 'listening' : ''}`}
                onClick={handleVoiceInput}
                disabled={isLoading || limitReached}
              >
                {isListening ? '⏹️ Stop' : '🎤 Voice Input'}
              </button>
              <span className="bai-hint">Changes appear in the calendars immediately.</span>
              <button className="send-btn" onClick={handleSend} disabled={isLoading || !input.trim() || limitReached}>
                {isLoading ? 'Working…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingsAIAgent;
